import { BrauzCustomer, loadBrauz, openBookAStylist } from 'helpers/brauz'

/**
 * Wraps a button that opens the Brauz "Book a Stylist" dialog.
 *
 * The SDK is fetched ahead of the click — on hover, on focus, or when the browser
 * goes idle, whichever lands first — so the dialog opens immediately in practice.
 * A click that beats the fetch awaits it, marking the trigger aria-busy meanwhile.
 *
 * Expected attributes:
 * - account-id: Brauz account ID (settings.brauz_account_id)
 * - customer-data: optional JSON prefill for the logged-in customer
 */
export class BrauzBookAStylist extends HTMLElement {
  static WARM_FALLBACK_DELAY = 2000

  private accountId = ''
  private customer?: BrauzCustomer
  private warmed = false
  private idleHandle: number | null = null
  private timeoutHandle: number | null = null

  private readonly boundWarm = () => this.warm()
  private readonly boundHandleClick = (event: Event) => this.handleClick(event)

  connectedCallback() {
    this.accountId = this.getAttribute('account-id')?.trim() ?? ''
    if (!this.accountId) return

    this.customer = this.parseCustomer()

    this.addEventListener('click', this.boundHandleClick)
    this.addEventListener('pointerenter', this.boundWarm)
    this.addEventListener('focusin', this.boundWarm)
    this.scheduleWarm()
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick)
    this.removeEventListener('pointerenter', this.boundWarm)
    this.removeEventListener('focusin', this.boundWarm)
    this.cancelScheduledWarm()
  }

  private parseCustomer(): BrauzCustomer | undefined {
    const raw = this.getAttribute('customer-data')
    if (!raw) return undefined

    try {
      return JSON.parse(raw) as BrauzCustomer
    } catch {
      return undefined
    }
  }

  private scheduleWarm() {
    if (typeof window.requestIdleCallback === 'function') {
      this.idleHandle = window.requestIdleCallback(this.boundWarm)
      return
    }
    this.timeoutHandle = window.setTimeout(this.boundWarm, BrauzBookAStylist.WARM_FALLBACK_DELAY)
  }

  private cancelScheduledWarm() {
    if (this.idleHandle !== null) {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.idleHandle)
      }
      this.idleHandle = null
    }
    if (this.timeoutHandle !== null) {
      window.clearTimeout(this.timeoutHandle)
      this.timeoutHandle = null
    }
  }

  private warm() {
    if (this.warmed) return
    this.warmed = true
    // Best effort: a failure here clears the cached promise, so the click retries.
    loadBrauz(this.accountId, this.customer).catch(() => undefined)
  }

  private async handleClick(event: Event) {
    const trigger = (event.target as HTMLElement | null)?.closest('button')
    if (!trigger || !this.contains(trigger)) return

    event.preventDefault()
    trigger.setAttribute('aria-busy', 'true')

    try {
      await openBookAStylist(this.accountId, this.customer)
    } catch (error) {
      console.error(error)
    } finally {
      trigger.removeAttribute('aria-busy')
    }
  }
}
